import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCALE_LABELS = {
  0: 'Nunca', '0': 'Nunca',
  1: 'A veces, leve', '1': 'A veces, leve',
  2: 'A veces, severo', '2': 'A veces, severo',
  3: 'Frecuente, leve', '3': 'Frecuente, leve',
  4: 'Frecuente, severo', '4': 'Frecuente, severo',
};

const BRISTOL_LABELS = {
  '1': 'Tipo 1 — Trozos duros separados',
  '2': 'Tipo 2 — Salchicha grumosa',
  '3': 'Tipo 3 — Salchicha con grietas',
  '4': 'Tipo 4 — Lisa y suave',
  '5': 'Tipo 5 — Trozos blandos',
  '6': 'Tipo 6 — Fragmentos blandos',
  '7': 'Tipo 7 — Completamente líquida',
};

const SECTIONS = [
  { name: 'Cabeza', icon: '🧠', questions: [
    { id: 'cabeza_1', text: '¿Sufres de dolor de cabeza?', type: 'scale' },
    { id: 'cabeza_2', text: '¿Te sientes débil?', type: 'scale' },
    { id: 'cabeza_3', text: '¿Tienes mareos?', type: 'scale' },
    { id: 'cabeza_4', text: '¿Padeces de insomnio?', type: 'scale' },
  ]},
  { name: 'Ojos', icon: '👁', questions: [
    { id: 'ojos_1', text: '¿Tienes lagrimeo o picor en los ojos?', type: 'scale' },
    { id: 'ojos_2', text: '¿Están tus ojos hinchados, rojos o con pestañas pegadas?', type: 'scale' },
    { id: 'ojos_3', text: '¿Tienes bolsas u ojeras debajo de los ojos?', type: 'scale' },
    { id: 'ojos_4', text: '¿Ves borroso o en túnel?', type: 'scale' },
  ]},
  { name: 'Oídos', icon: '👂', questions: [
    { id: 'oidos_1', text: '¿Sientes picor en los oídos?', type: 'scale' },
    { id: 'oidos_2', text: '¿Tienes dolor o infección de oído?', type: 'scale' },
    { id: 'oidos_3', text: '¿Tienes secreción amarillenta en los oídos?', type: 'scale' },
    { id: 'oidos_4', text: '¿Sufres de tinnitus o pérdida auditiva?', type: 'scale' },
  ]},
  { name: 'Nariz / Garganta', icon: '👃', questions: [
    { id: 'nariz_1', text: '¿Tienes la nariz congestionada?', type: 'scale' },
    { id: 'nariz_2', text: '¿Padeces de problemas nasales como sinusitis?', type: 'scale' },
    { id: 'nariz_3', text: '¿Presentas moqueo, estornudos, lagrimeo y picor?', type: 'scale' },
    { id: 'nariz_4', text: '¿Tienes ataques de estornudos?', type: 'scale' },
    { id: 'nariz_5', text: '¿Sientes exceso de moco?', type: 'scale' },
    { id: 'nariz_6', text: '¿Tienes tos crónica?', type: 'scale' },
    { id: 'nariz_7', text: '¿Necesitas limpiar tu garganta frecuentemente?', type: 'scale' },
    { id: 'nariz_8', text: '¿Tienes dolor de garganta, ronquera o pérdida de voz?', type: 'scale' },
    { id: 'nariz_9', text: '¿Están tu lengua, encías o labios hinchados?', type: 'scale' },
  ]},
  { name: 'Piel', icon: '🖐', questions: [
    { id: 'piel_1', text: '¿Tienes aftas o llagas?', type: 'scale' },
    { id: 'piel_2', text: '¿Padeces de acné?', type: 'scale' },
    { id: 'piel_3', text: '¿Tienes heridas que pican, erupciones o piel seca?', type: 'scale' },
    { id: 'piel_4', text: '¿Has notado pérdida de cabello?', type: 'scale' },
    { id: 'piel_5', text: '¿Sufres de enrojecimiento o sofocos?', type: 'scale' },
    { id: 'piel_6', text: '¿Sudas en exceso?', type: 'scale' },
  ]},
  { name: 'Corazón / Pulmones', icon: '❤', questions: [
    { id: 'corazon_1', text: '¿Sientes congestión en el pecho?', type: 'scale' },
    { id: 'corazon_2', text: '¿Padeces de asma o bronquitis?', type: 'scale' },
    { id: 'corazon_3', text: '¿Tienes dificultad para respirar?', type: 'scale' },
    { id: 'corazon_4', text: '¿Tienes latidos irregulares?', type: 'scale' },
    { id: 'corazon_5', text: '¿Tienes latidos cardíacos muy rápidos?', type: 'scale' },
    { id: 'corazon_6', text: '¿Sientes dolor en el pecho?', type: 'scale' },
  ]},
  { name: 'Tracto Digestivo', icon: '🍽', questions: [
    { id: 'digestivo_1', text: '¿Sufres de náuseas o vómitos?', type: 'scale' },
    { id: 'digestivo_2', text: '¿Tienes diarrea?', type: 'scale' },
    { id: 'digestivo_3', text: '¿Sufres de estreñimiento?', type: 'scale' },
    { id: 'digestivo_4', text: '¿Tienes hinchazón o abdomen distendido?', type: 'scale' },
    { id: 'digestivo_5', text: '¿Tienes eructos o exceso de gases?', type: 'scale' },
    { id: 'digestivo_6', text: '¿Tienes acidez?', type: 'scale' },
    { id: 'digestivo_7', text: '¿Sufres de dolor estomacal o intestinal?', type: 'scale' },
    { id: 'digestivo_8', text: '¿Cuántos días a la semana vas al baño?', type: 'days' },
    { id: 'digestivo_9', text: 'Tipo de heces frecuentes (Escala de Bristol):', type: 'bristol' },
  ]},
  { name: 'Articulaciones / Músculos', icon: '💪', questions: [
    { id: 'musculos_1', text: '¿Tienes dolores articulares?', type: 'scale' },
    { id: 'musculos_2', text: '¿Padeces de artritis o artrosis?', type: 'scale' },
    { id: 'musculos_3', text: '¿Sufres de rigidez o limitación de movimientos?', type: 'scale' },
    { id: 'musculos_4', text: '¿Tienes dolores musculares?', type: 'scale' },
  ]},
  { name: 'Energía / Actividad', icon: '⚡', questions: [
    { id: 'energia_1', text: '¿Te sientes débil o constantemente cansado?', type: 'scale' },
    { id: 'energia_2', text: '¿Sufres de fatiga o somnolencia durante el día?', type: 'scale' },
    { id: 'energia_3', text: '¿Te sientes apático o con letargo?', type: 'scale' },
    { id: 'energia_4', text: '¿Tienes episodios de hiperactividad?', type: 'scale' },
    { id: 'energia_5', text: '¿Tienes dificultad para descansar o relajarte?', type: 'scale' },
  ]},
  { name: 'Mente', icon: '🧘', questions: [
    { id: 'mente_1', text: '¿Tienes mala memoria?', type: 'scale' },
    { id: 'mente_2', text: '¿Sufres de confusión mental?', type: 'scale' },
    { id: 'mente_3', text: '¿Tienes problemas de concentración?', type: 'scale' },
    { id: 'mente_4', text: '¿Tienes mala coordinación motora?', type: 'scale' },
    { id: 'mente_5', text: '¿Te cuesta tomar decisiones?', type: 'scale' },
    { id: 'mente_6', text: '¿Repites sonidos o palabras al hablar?', type: 'scale' },
    { id: 'mente_7', text: '¿Pronuncias palabras de forma confusa?', type: 'scale' },
    { id: 'mente_8', text: '¿Tienes dificultades de aprendizaje?', type: 'scale' },
    { id: 'mente_9', text: '¿Tienes dificultad para mantener la atención?', type: 'scale' },
  ]},
  { name: 'Emociones', icon: '😊', questions: [
    { id: 'emociones_1', text: '¿Tienes cambios de humor frecuentes?', type: 'scale' },
    { id: 'emociones_2', text: '¿Sufres de ansiedad, miedo o nerviosismo?', type: 'scale' },
    { id: 'emociones_3', text: '¿Tienes episodios de rabia o irritabilidad?', type: 'scale' },
    { id: 'emociones_4', text: '¿Sufres de depresión?', type: 'scale' },
  ]},
  { name: 'Otros', icon: '📋', questions: [
    { id: 'otros_1', text: '¿Te irritan los olores de fragancias o detergentes?', type: 'scale' },
    { id: 'otros_2', text: '¿Tienes ganas frecuentes o urgentes de orinar?', type: 'scale' },
    { id: 'otros_3', text: '¿Sientes picazón o tienes secreción genital?', type: 'scale' },
    { id: 'otros_4', text: '¿Tienes hinchazón en pies, piernas o manos?', type: 'scale' },
  ]},
  { name: 'Comentarios Adicionales', icon: '📝', questions: [
    { id: 'comentarios_1', text: 'Diagnósticos clínicos en general', type: 'textarea' },
  ]},
];

const formatAnswer = (q, value) => {
  if (value === undefined || value === null || value === '') return 'Sin respuesta';
  if (q.type === 'scale') return SCALE_LABELS[value] || 'Sin respuesta';
  if (q.type === 'days') return `${value} día${value !== 1 ? 's' : ''} por semana`;
  if (q.type === 'bristol') return BRISTOL_LABELS[String(value)] || `Tipo ${value}`;
  if (q.type === 'textarea') return String(value) || 'Sin comentarios';
  return String(value);
};

const DistClientTestDetailPage = () => {
  const { clientId, testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testRes, clientsRes] = await Promise.all([
          axios.get(`${API}/health-tests/${testId}`),
          axios.get(`${API}/users/clients`),
        ]);
        setTest(testRes.data);
        const c = clientsRes.data.find(u => u.id === clientId);
        if (c) setClientName(c.name);
        // Expand all sections by default
        const expanded = {};
        SECTIONS.forEach(s => { expanded[s.name] = true; });
        setExpandedSections(expanded);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId, testId]);

  const toggleSection = (name) => {
    setExpandedSections(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-12 h-12 rounded-full border-4 border-violet-100 border-t-violet-500 animate-spin" /></div>;
  if (!test) return <div className="text-center py-12 text-gray-400">Test no encontrado</div>;

  const answers = test.answers || {};

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-8" data-testid="dist-test-detail-page">
      <button onClick={() => navigate(`/distributor/clients/${clientId}`)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-profile">
        <ArrowLeft size={16} /> Perfil de {clientName}
      </button>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Respuestas del Test
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Calendar size={14} />
            {new Date(test.completed_at).toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <Badge className="bg-violet-100 text-violet-700 text-xs">
            {clientName}
          </Badge>
        </div>
      </div>

      {/* Sections with answers */}
      <div className="space-y-3">
        {SECTIONS.map(section => {
          const isExpanded = expandedSections[section.name];
          const answeredCount = section.questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== '').length;

          return (
            <Card key={section.name} className="border border-gray-100 shadow-sm overflow-hidden" data-testid={`section-${section.name}`}>
              <button onClick={() => toggleSection(section.name)}
                className="w-full text-left p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{section.icon}</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{section.name}</p>
                    <p className="text-xs text-gray-400">{answeredCount} de {section.questions.length} respondidas</p>
                  </div>
                </div>
                {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {isExpanded && (
                <CardContent className="pt-0 pb-4 px-4">
                  <div className="space-y-3 border-t border-gray-100 pt-3">
                    {section.questions.map((q, idx) => {
                      const rawAnswer = answers[q.id];
                      const displayAnswer = formatAnswer(q, rawAnswer);
                      const hasAnswer = rawAnswer !== undefined && rawAnswer !== null && rawAnswer !== '';

                      return (
                        <div key={q.id} className="flex flex-col gap-1" data-testid={`answer-${q.id}`}>
                          <p className="text-sm text-gray-600">{idx + 1}. {q.text}</p>
                          <p className={`text-sm font-medium pl-4 ${hasAnswer ? 'text-gray-900' : 'text-gray-300 italic'}`}>
                            {displayAnswer}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DistClientTestDetailPage;
